import { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, LayersControl, Tooltip, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Snowflake, Fish, FishSymbol, Ticket, Waves, Loader2, MapPin, AlertTriangle, Anchor, LocateFixed, BedDouble, X, Wrench, Layers } from "lucide-react";
import { useAuth } from "../components/AuthContext";
import Navbar from "../components/Navbar";
import { useLang } from "../components/LangContext";
import { STR } from "../lib/i18n";
import { api } from "../lib/api";

function ClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, 12, { duration: 1.2 });
  }, [center, map]);
  return null;
}

const toPolygon = (geometry, kind) => {
  if (kind === "lake" && geometry.length >= 3) return geometry;
  const lats = geometry.map((p) => p[0]);
  const lons = geometry.map((p) => p[1]);
  const pad = 0.004;
  const minLat = Math.min(...lats) - pad;
  const maxLat = Math.max(...lats) + pad;
  const minLon = Math.min(...lons) - pad;
  const maxLon = Math.max(...lons) + pad;
  return [[minLat, minLon], [minLat, maxLon], [maxLat, maxLon], [maxLat, minLon]];
};

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const FK_COLORS = { fvof: "#C98755", other: "#3E7CB1", fjall: "#9C7BB8", inatur: "#5BA579", dk: "#D95D4E" };
const FK_LABEL_COLORS = { fvof: "#EBB077", other: "#8FC0EE", fjall: "#CDB4E8", inatur: "#8FD3AE", dk: "#F09A8E" };
const FPI_COLOR = (f) => (f >= 34 ? "#14532d" : f >= 28 ? "#3f9142" : f >= 20 ? "#EAB308" : f >= 14 ? "#F97316" : "#DC2626");
const FPI_PART_KEYS = ["bpp", "markbuffert", "ca_index", "vattenkemi", "bottenfauna", "hydrologi", "temperaturtyp", "djupvariation"];

// Hotspot-typer: färg + SVG-ikon per typ
const HS_COLORS = { predator_point: "#E4572E", hump: "#F2C979", weedline: "#4C9A5B", habitat_edge: "#9C7BB8" };
const HS_PATHS = {
  predator_point: "M12 2 L22 20 L12 15.5 L2 20 Z", // spetsig udde
  hump: "M2 18 Q7 6 12 6 Q17 6 22 18 Z", // grundtopp/båge
  weedline: "M12 3 C18 7 19 15 12 21 C5 15 6 7 12 3 Z", // blad
  habitat_edge: "M4 4 H14 V14 H4 Z M10 10 H20 V20 H10 Z", // mötande ytor
};
const HS_ICONS = {};
const hsIcon = (type) => {
  if (!HS_ICONS[type]) {
    const color = HS_COLORS[type] || "#C98755";
    HS_ICONS[type] = L.divIcon({
      className: "",
      html: `<svg width="24" height="24" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.7))"><path d="${HS_PATHS[type]}" fill="${color}" stroke="#0F1412" stroke-width="1.5"/></svg>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
  }
  return HS_ICONS[type];
};

function Badge({ demo, model, map, web, fk, svar, nve, syke, vanda, slu, gbif, inatur, aiv, t }) {
  const text = demo ? t.demoBadge : web ? t.webBadge : inatur ? t.inaturBadge : aiv ? t.aivBadge : fk ? t.fkBadge : svar ? t.svarBadge : nve ? t.nveBadge : syke ? t.sykeBadge : vanda ? t.vandaBadge : slu ? t.sluBadge : gbif ? t.gbifBadge : map ? t.modelBadgeMap : model ? t.modelBadge : t.realBadge;
  return (
    <span
      data-testid={demo ? "demo-badge" : "real-badge"}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${
        demo
          ? "bg-[#E8C547]/15 text-[#E8C547]"
          : model
            ? "bg-[#3E7CB1]/20 text-[#8FB8DD]"
            : "bg-[#4C9A5B]/15 text-[#7BC98C]"
      }`}
    >
      {text}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <p className="overline-label text-[#C98755]">{title}</p>
      <div className="mt-2 text-sm leading-relaxed text-[#EBE9E0]/85">{children}</div>
    </div>
  );
}

function AssessmentView({ data, labels }) {
  return (
    <>
      {Object.entries(data.assessment || {}).map(([key, value]) => (
        <Section key={key} title={labels[key] || key}>
          {Array.isArray(value) ? (
            <ul className="list-disc space-y-1 pl-5">
              {value.map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
          ) : (
            <p>{value}</p>
          )}
        </Section>
      ))}
    </>
  );
}

export default function Verktyg() {
  const { lang } = useLang();
  const t = STR[lang].tool;

  const [water, setWater] = useState(null);
  const [clickPoint, setClickPoint] = useState(null);
  const [flyCenter, setFlyCenter] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [stays, setStays] = useState(null);
  const [staysLoading, setStaysLoading] = useState(false);
  const [staysOpen, setStaysOpen] = useState(false);
  const [fpiWaters, setFpiWaters] = useState([]);
  const [fpiMin, setFpiMin] = useState(9);
  const [fpiVisible, setFpiVisible] = useState(false);
  const [hsTypes, setHsTypes] = useState({});
  const [hsMaxShore, setHsMaxShore] = useState(600);
  const { user } = useAuth();

  // Historik + ackumulerade FPI-vatten hämtas bara för inloggad admin (integritet)
  const loadHistory = useCallback(() => {
    if (!user) {
      setHistory([]);
      return;
    }
    api.get("/verktyg/history").then((r) => setHistory(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    loadHistory();
    if (!user) {
      setFpiWaters([]);
      return;
    }
    api.get("/verktyg/fpi-all").then((r) => setFpiWaters(r.data)).catch(() => {});
  }, [loadHistory, user]);

  // FPI beräknas automatiskt i bakgrunden för varje valt vatten (cachas i backend)
  const loadFpi = useCallback((w) => {
    if (!w?.found || !w.geometry?.length) return;
    api
      .post("/verktyg/fpi", { lake_polygon: toPolygon(w.geometry, w.kind), water_name: w.name })
      .then((r) => {
        if (r.data.found) {
          const norm = {
            ...r.data,
            parts: Object.fromEntries(Object.entries(r.data.parts || {}).map(([k, v]) => [k, v?.score ?? v])),
          };
          setFpiWaters((prev) => [...prev.filter((x) => x.name !== r.data.name), norm]);
        }
      })
      .catch(() => {});
  }, []);

  const lookupWater = async (lat, lon) => {
    setError("");
    setResult(null);
    setStays(null);
    setClickPoint([lat, lon]);
    try {
      const r = await api.post("/verktyg/water", { lat, lon });
      if (r.data.found) {
        setWater(r.data);
        setFlyCenter(r.data.center);
        loadFpi(r.data);
      } else {
        setWater(null);
        setError(t.noWater);
      }
    } catch {
      setError(t.noWater);
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    setSearching(true);
    setError("");
    try {
      const r = await api.get("/verktyg/search", { params: { q: searchQ } });
      setSearchResults(r.data);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const runAnalysis = async (kind, endpoint) => {
    if (!water || loading) return;
    setLoading(kind);
    setError("");
    setStays(null);
    setStaysOpen(false);
    setFpiVisible(false);
    try {
      const r = await api.post(endpoint, {
        lake_polygon: toPolygon(water.geometry, water.kind),
        water_name: water.name,
      });
      setResult({ kind, data: r.data });
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || "Analysen misslyckades, försök igen.");
    } finally {
      setLoading("");
    }
  };

  const buttons = [
    { kind: "ice", endpoint: "/analyze-ice", label: t.analyzeIce, Icon: Snowflake },
    { kind: "fishing", endpoint: "/analyze-fishing", label: t.hotspots, Icon: Fish },
    { kind: "fvof", endpoint: "/lookup-fvof", label: t.fvof, Icon: Ticket },
    { kind: "hydro", endpoint: "/lookup-hydro", label: t.hydro, Icon: Waves },
    { kind: "depth", endpoint: "/verktyg/depthmap", label: t.depthMap, Icon: Anchor },
    { kind: "fish", endpoint: "/verktyg/fish", label: t.fishSpecies, Icon: FishSymbol },
    { kind: "measures", endpoint: "/verktyg/measures", label: t.measures, Icon: Wrench },
  ];

  const openStays = async () => {
    if (!water || staysLoading) return;
    setStaysOpen(true);
    setStaysLoading(true);
    setFpiVisible(false);
    setError("");
    try {
      const r = await api.post("/accommodations", {
        lake_polygon: toPolygon(water.geometry, water.kind),
        water_name: water.name,
      });
      setStays(r.data);
    } catch (err) {
      setStays(null);
      setError(err.response?.data?.detail || t.staysError);
    } finally {
      setStaysLoading(false);
    }
  };

  // Stäng boendemodalen med Escape
  useEffect(() => {
    if (!staysOpen) return;
    const onKey = (e) => e.key === "Escape" && setStaysOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [staysOpen]);

  const [gpsPoint, setGpsPoint] = useState(null);
  const [locating, setLocating] = useState(false);

  const locateMe = () => {
    if (!navigator.geolocation || locating) return;
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setGpsPoint(p);
        setFlyCenter(p);
        setLocating(false);
      },
      () => {
        setError(t.locateError);
        setLocating(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const zones = result?.data?.zones || [];
  const fkAreas = result?.kind === "fvof" ? result?.data?.fiskekartan?.areas || [] : [];
  const hotspots = result?.kind === "fishing" ? result?.data?.hotspots || [] : [];
  const hsMeta = result?.kind === "fishing" ? result?.data?.hotspot_meta : null;
  const hsTypesPresent = [...new Set(hotspots.map((h) => h.type))];
  const visibleHotspots = hotspots.filter((h) => hsTypes[h.type] !== false && (hsMaxShore >= 600 || (h.shore_m ?? 0) <= hsMaxShore));
  const currentFpi = fpiWaters.find((w) => w.name === water?.name);

  return (
    <div data-testid="tool-page" className="min-h-screen bg-[#0F1412] text-[#EBE9E0]">
      <Navbar />
      <div className="flex flex-col pt-[68px] lg:h-screen lg:flex-row">
        {/* SIDOPANEL */}
        <aside className="order-2 w-full overflow-y-auto border-t border-[#28332E] lg:order-1 lg:w-[420px] lg:border-r lg:border-t-0">
          <div className="p-6">
            <p className="overline-label text-[#C98755]">{t.overline}</p>
            <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight" data-testid="tool-title">{t.title}</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#8A998E]">{t.intro}</p>

            <form onSubmit={doSearch} className="mt-6 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A998E]" strokeWidth={1.5} />
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  data-testid="water-search-input"
                  placeholder={t.searchPlaceholder}
                  className="w-full border border-[#28332E] bg-[#161D1A] py-2.5 pl-10 pr-3 text-sm text-[#EBE9E0] outline-none placeholder:text-[#8A998E]/60 focus:border-[#C98755]"
                />
              </div>
              <button
                type="submit"
                data-testid="water-search-button"
                className="border border-[#C98755] px-4 text-sm font-semibold text-[#C98755] transition-colors duration-300 hover:bg-[#C98755] hover:text-[#0F1412]"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : t.searchButton}
              </button>
            </form>
            {searchResults.length > 0 && (
              <div className="mt-2 divide-y divide-[#28332E] border border-[#28332E] bg-[#161D1A]" data-testid="search-results">
                {searchResults.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`search-result-${r.id}`}
                    onClick={() => {
                      setSearchResults([]);
                      setError("");
                      setResult(null);
                      setStays(null);
                      setWater({ found: true, name: r.name, kind: r.kind, geometry: r.geometry, center: r.center });
                      setFlyCenter(r.center);
                      loadFpi({ found: true, name: r.name, kind: r.kind, geometry: r.geometry });
                    }}
                    className="block w-full px-4 py-3 text-left text-sm text-[#EBE9E0]/85 transition-colors duration-200 hover:bg-[#0F1412] hover:text-[#C98755]"
                  >
                    {r.name} <span className="text-xs text-[#8A998E]">({r.kind === "lake" ? t.lake : t.river})</span>
                  </button>
                ))}
              </div>
            )}

            {error && (
              <p data-testid="tool-error" className="mt-4 flex items-start gap-2 border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm text-red-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
              </p>
            )}

            {/* FPI-filter och teckenförklaring – visas bara när FPI-lagret är aktivt */}
            {fpiVisible && (
              <div data-testid="fpi-filter" className="mt-6 border border-[#28332E] bg-[#161D1A] p-4">
              <p className="overline-label text-[#C98755]">{t.fpi.title}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-[#8A998E]">{t.fpi.legendHint}</p>
              <div className="mt-3 flex items-center gap-3">
                {[40, 33, 27, 19, 13].map((v) => (
                  <span key={v} className="flex items-center gap-1.5 text-[10px] text-[#8A998E]">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: FPI_COLOR(v) }} />
                    {v === 40 ? "34–40" : v === 33 ? "28–33" : v === 27 ? "20–27" : v === 19 ? "14–19" : "9–13"}
                  </span>
                ))}
              </div>
              <label className="mt-4 block text-xs text-[#8A998E]" htmlFor="fpi-min-range">
                {t.fpi.filterLabel}: <span className="font-semibold text-[#EBE9E0]" data-testid="fpi-min-value">{fpiMin}</span>
              </label>
              <input
                id="fpi-min-range"
                type="range"
                min={9}
                max={40}
                step={1}
                value={fpiMin}
                onChange={(e) => setFpiMin(Number(e.target.value))}
                data-testid="fpi-min-range"
                className="mt-2 w-full accent-[#C98755]"
              />
              <p data-testid="fpi-count" className="mt-1.5 text-[11px] text-[#8A998E]/80">{t.fpi.count(fpiWaters.filter((w) => w.fpi >= fpiMin).length, fpiWaters.length)}</p>
              </div>
            )}

            {water ? (
              <div className="mt-6 border border-[#28332E] bg-[#161D1A] p-4" data-testid="selected-water">
                <p className="overline-label text-[#8A998E]">{t.selectedWater}</p>
                <p className="mt-2 flex items-center gap-2 font-serif text-xl text-[#EBE9E0]">
                  <MapPin className="h-4 w-4 text-[#C98755]" /> {water.name}
                </p>
                <p className="mt-1 text-xs tracking-widest text-[#8A998E]">
                  {water.kind === "lake" ? t.lake : t.river} · {t.coordinates}: {water.center[0].toFixed(4)}, {water.center[1].toFixed(4)}
                </p>
              </div>
            ) : (
              <p className="mt-6 text-sm italic text-[#8A998E]" data-testid="click-hint">{t.clickHint}</p>
            )}

            <div className="mt-6 grid grid-cols-1 gap-2">
              {buttons.map(({ kind, endpoint, label, Icon }) => (
                <button
                  key={kind}
                  onClick={() => runAnalysis(kind, endpoint)}
                  disabled={!water || !!loading}
                  data-testid={`action-${kind}-button`}
                  className="flex items-center gap-3 border border-[#28332E] bg-[#161D1A] px-4 py-3.5 text-sm font-semibold text-[#EBE9E0] transition-colors duration-300 enabled:hover:border-[#C98755] enabled:hover:text-[#C98755] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {loading === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" strokeWidth={1.5} />}
                  {loading === kind ? t.analysing : label}
                </button>
              ))}
              <button
                onClick={openStays}
                disabled={!water || !!loading}
                data-testid="action-stay-button"
                className="flex items-center gap-3 border border-[#28332E] bg-[#161D1A] px-4 py-3.5 text-sm font-semibold text-[#EBE9E0] transition-colors duration-300 enabled:hover:border-[#C98755] enabled:hover:text-[#C98755] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <BedDouble className="h-4 w-4" strokeWidth={1.5} />
                {t.stay}
              </button>
              <button
                onClick={() => setFpiVisible((v) => !v)}
                disabled={!water || !!loading}
                data-testid="action-fpi-button"
                aria-pressed={fpiVisible}
                className={`flex items-center gap-3 border px-4 py-3.5 text-sm font-semibold transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-40 ${
                  fpiVisible
                    ? "border-[#C98755] bg-[#C98755]/10 text-[#C98755]"
                    : "border-[#28332E] bg-[#161D1A] text-[#EBE9E0] enabled:hover:border-[#C98755] enabled:hover:text-[#C98755]"
                }`}
              >
                <Layers className="h-4 w-4" strokeWidth={1.5} />
                {t.fpi.toggle}
              </button>
            </div>

            {/* RESULTATPANEL */}
            {result && (
              <div className="mt-8 border border-[#28332E] bg-[#161D1A] p-5" data-testid="result-panel">
                <div className="flex items-center justify-between gap-3">
                  <p className="overline-label text-[#8A998E]">
                    {t.result} · {t.kindNames[result.kind]}
                  </p>
                  <Badge demo={result.data.demo} model={result.data.model} map={result.data.map} web={result.data.web} fk={result.data.fiskekartan && !result.data.source && !result.data.inatur} inatur={result.data.inatur} aiv={result.data.aiv} svar={result.data.svar} nve={result.data.nve} syke={result.data.syke} vanda={result.data.vanda} slu={result.data.slu} gbif={result.data.gbif} t={t} />
                </div>

                {zones.length > 0 && (
                  <Section title={t.legend}>
                    <div className="flex flex-wrap gap-2">
                      {zones.map((z) => (
                        <span key={z.type} className="inline-flex items-center gap-2 text-xs text-[#EBE9E0]/80">
                          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: z.color }} />
                          {z.label}
                        </span>
                      ))}
                    </div>
                  </Section>
                )}

                {result.kind === "ice" && <AssessmentView data={result.data} labels={t.iceLabels} />}
                {result.kind === "fishing" && <AssessmentView data={result.data} labels={t.fishLabels} />}

                {result.kind === "fishing" && hotspots.length > 0 && (
                  <div data-testid="hotspot-filter" className="mt-5 border-t border-[#28332E] pt-4">
                    <p className="overline-label text-[#C98755]">{t.hs.title}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {hsTypesPresent.map((type) => (
                        <label key={type} className="flex cursor-pointer items-center gap-2 text-xs text-[#EBE9E0]/85">
                          <input
                            type="checkbox"
                            checked={hsTypes[type] !== false}
                            onChange={() => setHsTypes((prev) => ({ ...prev, [type]: prev[type] === false }))}
                            data-testid={`hotspot-toggle-${type}`}
                            className="accent-[#C98755]"
                          />
                          <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: HS_COLORS[type] }} />
                          {t.hs.types[type] || type}
                        </label>
                      ))}
                    </div>
                    <label className="mt-4 block text-xs text-[#8A998E]" htmlFor="hotspot-shore-range">
                      {t.hs.shoreFilter}{" "}
                      <span className="font-semibold text-[#EBE9E0]" data-testid="hotspot-shore-value">
                        {hsMaxShore >= 600 ? t.hs.allDistances : `${hsMaxShore} ${t.hs.shoreUnit}`}
                      </span>
                    </label>
                    <input
                      id="hotspot-shore-range"
                      type="range"
                      min={25}
                      max={600}
                      step={25}
                      value={hsMaxShore}
                      onChange={(e) => setHsMaxShore(Number(e.target.value))}
                      data-testid="hotspot-shore-range"
                      className="mt-2 w-full accent-[#C98755]"
                    />
                    {hsMeta?.scene_date && (
                      <p className="mt-2 text-[11px] text-[#8A998E]/80" data-testid="hotspot-scene-info">
                        {t.hs.scene(hsMeta.scene_date)}
                        {hsMeta.partial ? ` · ${t.hs.partial}` : ""}
                      </p>
                    )}
                    {hsMeta?.satellite === "no_scene" && (
                      <p className="mt-2 text-[11px] text-[#8A998E]/80">{t.hs.noSat}</p>
                    )}
                    <p className="mt-2 text-[11px] italic text-[#8A998E]/70">{t.hs.disclaimer}</p>
                  </div>
                )}

                {result.kind === "fvof" && (
                  <div data-testid="fvof-panel">
                    {result.data.fisketegn && (
                      <div data-testid="fisketegn-note" className="mt-5 border border-[#C98755]/40 bg-[#C98755]/10 p-4">
                        <p className="text-xs leading-relaxed text-[#EBE9E0]/85">{t.fvofLabels.fisketegnNote}</p>
                        <a
                          href="https://fisketegn.dk/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#C98755] hover:underline"
                        >
                          fisketegn.dk →
                        </a>
                      </div>
                    )}
                    {!result.data.found ? (
                      <div className="mt-5">
                        <p className="text-sm leading-relaxed text-[#EBE9E0]/85">{t.noFvof}</p>
                        <a
                          href={result.data.search_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="fvof-search-link"
                          className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#C98755] hover:underline"
                        >
                          {result.data.search_provider === "inatur" ? t.fvofLabels.searchInatur : result.data.search_provider === "fiskekort" ? t.fvofLabels.searchFiskekort : t.fvofLabels.searchIfiske} →
                        </a>
                      </div>
                    ) : (
                      <>
                        {result.data.fiskekartan?.areas?.length > 0 && (
                          <Section title={result.data.inatur ? t.fvofLabels.inaturTitle : result.data.fisketegn ? t.fvofLabels.dkTitle : t.fvofLabels.fiskekartanTitle}>
                            {["fvof", "other", "fjall", "inatur", "dk"].map((cat) => {
                              const items = result.data.fiskekartan.areas.filter((a) => a.category === cat);
                              if (!items.length) return null;
                              return (
                                <div key={cat} className="mt-4 first:mt-0" data-testid={`fk-group-${cat}`}>
                                  <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#8A998E]">
                                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: FK_COLORS[cat] }} />
                                    {t.fvofLabels.cats[cat]}
                                  </p>
                                  <ul className="mt-2 space-y-2.5">
                                    {items.map((a) => (
                                      <li key={a.name} data-testid={`fk-area-${a.name}`}>
                                        <a href={a.ifiske_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#EBE9E0] transition-colors duration-200 hover:text-[#C98755]">
                                          {a.name}
                                        </a>
                                        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#8A998E]">
                                          {a.state_water && (
                                            <span className="inline-flex items-center rounded-full bg-[#9C7BB8]/15 px-2 py-0.5 font-semibold text-[#C4AEE0]">
                                              {t.fvofLabels.stateWater}
                                            </span>
                                          )}
                                          {[a.org_type, a.status].filter(Boolean).join(" · ")}
                                          {!a.covers_water && a.distance_km > 0 && (
                                            <span className="text-[#8A998E]/70">· ca {a.distance_km} {t.fvofLabels.kmAway}</span>
                                          )}
                                        </p>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                            <a
                              href={result.data.fiskekartan.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="fk-source-link"
                              className="mt-3 inline-block text-xs text-[#C98755] hover:underline"
                            >
                              {t.fvofLabels.source}: {result.data.fiskekartan.source}
                            </a>
                          </Section>
                        )}
                        {result.data.fvof_name && (
                          <>
                            <Section title={t.fvofLabels.ifiskeTitle}>
                              <p className="font-serif text-lg">{result.data.fvof_name}</p>
                              <a
                                href={result.data.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-block text-xs text-[#C98755] hover:underline"
                              >
                                {t.fvofLabels.source}: {result.data.source}
                              </a>
                            </Section>
                        {result.data.rules?.length > 0 && (
                          <Section title={t.fvofLabels.rules}>
                            <ul className="list-disc space-y-1 pl-5">
                              {result.data.rules.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </Section>
                        )}
                        {result.data.season && <Section title={t.fvofLabels.season}><p>{result.data.season}</p></Section>}
                        {result.data.min_size && <Section title={t.fvofLabels.minSize}><p>{result.data.min_size}</p></Section>}
                        {result.data.card_types?.length > 0 && (
                          <Section title={t.fvofLabels.cardTypes}>
                            <ul className="space-y-1.5">
                              {result.data.card_types.map((c, i) => (
                                <li key={i} className="flex justify-between gap-4 border-b border-[#28332E] pb-1.5 text-sm">
                                  <span className="text-[#EBE9E0]/85">{c.name}</span>
                                  <span className="shrink-0 font-semibold text-[#C98755]">{c.price}</span>
                                </li>
                              ))}
                            </ul>
                          </Section>
                        )}
                        {result.data.fisketips?.length > 0 && (
                          <Section title={t.fvofLabels.tips}>
                            <ul className="list-disc space-y-1 pl-5">
                              {result.data.fisketips.map((tip, i) => (
                                <li key={i}>{tip}</li>
                              ))}
                            </ul>
                          </Section>
                        )}
                        {result.data.purchase_links?.length > 0 && (
                          <Section title={t.fvofLabels.purchase}>
                            {result.data.purchase_links.map((l) => (
                              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="text-[#C98755] hover:underline">
                                {l.name}
                              </a>
                            ))}
                          </Section>
                        )}
                        {result.data.contact && <Section title={t.fvofLabels.contact}><p>{result.data.contact}</p></Section>}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {result.kind === "fish" && (
                  <div data-testid="fish-panel">
                    {!result.data.found ? (
                      <p className="mt-5 text-sm leading-relaxed text-[#EBE9E0]/85">{t.fishPanel.noData}</p>
                    ) : (
                      <>
                        <Section title={result.data.lake_name}>
                          <a
                            href={result.data.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="fish-source-link"
                            className="mt-1 inline-block text-xs text-[#C98755] hover:underline"
                          >
                            {t.fishPanel.source}: {result.data.source}
                          </a>
                          {result.data.year_range && (
                            <p className="mt-1 text-xs text-[#8A998E]">
                              {t.fishPanel.yearRange}: {result.data.year_range}
                            </p>
                          )}
                        </Section>
                        <Section title={t.fishPanel.common}>
                          <ul className="space-y-2.5" data-testid="fish-species-list">
                            {result.data.species.map((s) => {
                              const displayName = lang === "en" && s.name_en ? s.name_en : s.name;
                              const unit = result.data.gbif ? t.fishPanel.obs : t.fishPanel.count;
                              return (
                                <li key={s.name} data-testid={`fish-species-${s.name}`}>
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-semibold text-[#EBE9E0]">{displayName}</span>
                                    <span className="text-xs text-[#8A998E]">
                                      {s.count} {unit} · {s.share}%
                                    </span>
                                  </div>
                                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#28332E]">
                                    <div
                                      className="h-full rounded-full bg-[#C98755]"
                                      style={{ width: `${Math.max(s.share, 2)}%` }}
                                    />
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          <p className="mt-4 text-xs italic leading-relaxed text-[#8A998E]">
                            {result.data.gbif ? t.fishPanel.gbifNote : t.fishPanel.note}
                          </p>
                        </Section>
                        {result.data.nors && (
                          <Section title={t.fishPanel.norsTitle}>
                            <div data-testid="fish-nors-complement">
                              <a
                                href={result.data.nors.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mb-2 inline-block text-xs text-[#C98755] hover:underline"
                              >
                                {t.fishPanel.source}: {result.data.nors.source}
                              </a>
                              <ul className="space-y-1.5">
                                {result.data.nors.species.map((s) => (
                                  <li key={s.name} className="flex items-baseline justify-between gap-2 text-sm">
                                    <span className="text-[#EBE9E0]/90">{lang === "en" && s.name_en ? s.name_en : s.name}</span>
                                    <span className="text-xs text-[#8A998E]">{s.count} {t.fishPanel.count}</span>
                                  </li>
                                ))}
                              </ul>
                              {result.data.nors.year_range && (
                                <p className="mt-2 text-xs text-[#8A998E]">
                                  {result.data.nors.fish_total} {t.fishPanel.norsCaught} · {t.fishPanel.yearRange.toLowerCase()}: {result.data.nors.year_range}
                                </p>
                              )}
                            </div>
                          </Section>
                        )}
                      </>
                    )}
                  </div>
                )}

                {result.kind === "measures" && (
                  <div data-testid="measures-panel">
                    {result.data.outside_sweden ? (
                      <p className="mt-5 text-sm leading-relaxed text-[#EBE9E0]/85">{t.measuresLabels.outside}</p>
                    ) : !result.data.found ? (
                      <p className="mt-5 text-sm leading-relaxed text-[#EBE9E0]/85">{t.measuresLabels.empty}</p>
                    ) : (
                      <div className="mt-5 space-y-3">
                        <p className="overline-label text-[#C98755]">{t.measuresLabels.title} · {result.data.total_nearby} st</p>
                        {result.data.measures.map((m, i) => (
                          <div key={i} data-testid={`measure-row-${i}`} className="border border-[#28332E] bg-[#161D1A] p-4">
                            <div className="flex items-start justify-between gap-3">
                              <p className="font-serif text-base font-medium leading-snug text-[#EBE9E0]">{m.name}</p>
                              <span className="shrink-0 text-xs font-semibold tracking-wide text-[#C98755]">{m.distance_km.toFixed(1)} {t.kmFrom}</span>
                            </div>
                            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#8A998E]">
                              {m.category && (
                                <span className="inline-flex items-center rounded-full border border-[#C98755]/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#C98755]">
                                  {m.category}
                                </span>
                              )}
                              {m.year && <span>{m.year}</span>}
                              {m.types?.length > 0 && <span>{m.types.join(", ")}</span>}
                            </p>
                            {m.comment && <p className="mt-2 text-xs leading-relaxed text-[#EBE9E0]/70">{m.comment}</p>}
                          </div>
                        ))}
                        {result.data.synced_at && (
                          <p className="text-[11px] text-[#8A998E]/70">{t.measuresLabels.synced}: {String(result.data.synced_at).slice(0, 10)}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {result.kind === "depth" && (
                  <div data-testid="depth-panel">
                    {!result.data.found ? (
                      <p className="mt-5 text-sm leading-relaxed text-[#EBE9E0]/85">{t.depthLabels.noDepth}</p>
                    ) : (
                      <>
                        <Section title="SMHI SVAR">
                          <p className="font-serif text-lg">{result.data.lake_name}</p>
                          <a
                            href={result.data.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="depth-source-link"
                            className="mt-1 inline-block text-xs text-[#C98755] hover:underline"
                          >
                            {t.depthLabels.source}: {result.data.source}
                          </a>
                        </Section>
                        {result.data.max_depth != null && (
                          <Section title={t.depthLabels.maxDepth}>
                            <p className="font-serif text-2xl" data-testid="depth-max">{result.data.max_depth} m</p>
                          </Section>
                        )}
                        {result.data.mean_depth != null && (
                          <Section title={t.depthLabels.meanDepth}><p>{result.data.mean_depth} m</p></Section>
                        )}
                        {result.data.volume_km3 != null && (
                          <Section title={t.depthLabels.volume}><p>{result.data.volume_km3} km³</p></Section>
                        )}
                        {result.data.area_km2 != null && (
                          <Section title={t.depthLabels.area}><p>{result.data.area_km2} km²</p></Section>
                        )}
                        {result.data.elevation_m != null && (
                          <Section title={t.depthLabels.elevation}><p>{result.data.elevation_m} m</p></Section>
                        )}
                        {result.data.survey_year && (
                          <Section title={t.depthLabels.surveyed}><p>{result.data.survey_year}</p></Section>
                        )}
                        {result.data.map_count > 0 && (
                          <Section title={t.depthLabels.maps}>
                            <p>{result.data.map_count} {t.depthLabels.mapsAvailable}</p>
                            {result.data.map_notes?.length > 0 && (
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-[#8A998E]">
                                {result.data.map_notes.map((n, i) => (
                                  <li key={i}>{n}</li>
                                ))}
                              </ul>
                            )}
                            <a
                              href={result.data.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="depth-download-link"
                              className="mt-3 inline-flex items-center gap-2 border border-[#C98755] px-4 py-2 text-sm font-semibold text-[#C98755] transition-colors duration-300 hover:bg-[#C98755] hover:text-[#0F1412]"
                            >
                              {t.depthLabels.download} ↓
                            </a>
                            <p className="mt-3 text-xs italic text-[#8A998E]">{t.depthLabels.note}</p>
                          </Section>
                        )}
                      </>
                    )}
                    {/* Extern utlänk: C-MAP Genesis crowdsourcade ekolodskartor */}
                    <div className="mt-6 border-t border-[#28332E] pt-4">
                      <a
                        href="https://www.genesismaps.com/SocialMap"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="genesis-link"
                        className="inline-flex items-center gap-2 border border-[#28332E] px-4 py-2 text-sm font-semibold text-[#EBE9E0] transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
                      >
                        {t.depthLabels.genesis}
                      </a>
                      <p className="mt-2 text-[11px] leading-relaxed text-[#8A998E]/80">{t.depthLabels.genesisNote}</p>
                    </div>
                  </div>
                )}

                {result.kind === "hydro" && (
                  <div data-testid="hydro-panel">
                    {result.data.flow_rate !== null && (
                      <Section title={t.hydroLabels.flowRate}>
                        <p className="font-serif text-2xl">{result.data.flow_rate} m³/s</p>
                      </Section>
                    )}
                    {result.data.water_level !== null && (
                      <Section title={t.hydroLabels.waterLevel}>
                        <p className="font-serif text-2xl">{result.data.water_level} {result.data.level_unit || "cm"}</p>
                        {result.data.level_station_name && (
                          <p className="mt-1 text-xs text-[#8A998E]">{t.hydroLabels.levelStation}: {result.data.level_station_name}</p>
                        )}
                        {result.data.level_archived && (
                          <p className="mt-1 text-xs font-semibold text-amber-400">{t.hydroLabels.archivedStation}</p>
                        )}
                      </Section>
                    )}
                    <Section title={t.hydroLabels.trend}><p className="capitalize">{result.data.trend}</p></Section>
                    <Section title={t.hydroLabels.station}>
                      <p>{result.data.station_name} · {t.hydroLabels.distance}: {result.data.distance_km} km</p>
                      {result.data.flow_archived && (
                        <p className="mt-1 text-xs font-semibold text-amber-400" data-testid="hydro-archived-note">{t.hydroLabels.archivedStation}</p>
                      )}
                    </Section>
                    {result.data.history?.length > 0 && (
                      <Section title={t.hydroLabels.history}>
                        <ul className="space-y-1">
                          {result.data.history.slice(-6).map((h, i) => (
                            <li key={i} className="flex justify-between text-xs">
                              <span className="text-[#8A998E]">{fmtTime(h.t)}</span>
                              <span>{h.v} m³/s</span>
                            </li>
                          ))}
                        </ul>
                      </Section>
                    )}
                    <Section title={t.hydroLabels.updated}>
                      <p className="text-xs">{result.data.last_updated ? fmtTime(result.data.last_updated) : "–"}</p>
                    </Section>
                  </div>
                )}
              </div>
            )}

            {/* HISTORIK – visas bara för inloggad admin (integritet) */}
            {user && (
            <div className="mt-10" data-testid="analysis-history">
              <p className="overline-label text-[#8A998E]">{t.history}</p>
              {history.length === 0 ? (
                <p className="mt-3 text-sm italic text-[#8A998E]">{t.noHistory}</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {history.map((h, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate text-[#EBE9E0]/85">
                        {t.kindNames[h.kind] || h.kind} · {h.water_name}
                      </span>
                      <span className="shrink-0 text-xs text-[#8A998E]">{fmtTime(h.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}
          </div>
        </aside>

        {/* KARTA */}
        <div className="relative order-1 h-[55vh] lg:order-2 lg:h-auto lg:flex-1" data-testid="tool-map">
          <button
            onClick={locateMe}
            data-testid="gps-locate-button"
            title={t.locate}
            className="absolute left-3 top-20 z-[1000] flex h-11 w-11 items-center justify-center border border-[#28332E] bg-[#161D1A]/90 text-[#EBE9E0] shadow-lg backdrop-blur transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
          >
            {locating ? <Loader2 className="h-5 w-5 animate-spin" /> : <LocateFixed className="h-5 w-5" strokeWidth={1.5} />}
          </button>
          <MapContainer center={[62.5, 15.5]} zoom={5} className="h-full w-full" scrollWheelZoom>
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satellit">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Tiles &copy; Esri"
                  maxZoom={18}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Karta">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.Overlay name={t.osmLayer}>
                <TileLayer
                  url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openseamap.org">OpenSeaMap</a>'
                  maxZoom={18}
                />
              </LayersControl.Overlay>
            </LayersControl>
            <ClickHandler onClick={(latlng) => lookupWater(latlng.lat, latlng.lng)} />
            <FlyTo center={flyCenter} />
            {clickPoint && (
              <CircleMarker center={clickPoint} radius={6} pathOptions={{ color: "#C98755", fillColor: "#C98755", fillOpacity: 0.9 }} />
            )}
            {gpsPoint && (
              <CircleMarker
                center={gpsPoint}
                radius={8}
                pathOptions={{ color: "#EBE9E0", weight: 2, fillColor: "#3E7CB1", fillOpacity: 0.9 }}
                data-testid="gps-marker"
              >
                <Tooltip>{t.locate}</Tooltip>
              </CircleMarker>
            )}
            {/* FPI-kartlager: visas bara när FPI-filtret är aktiverat */}
            {fpiVisible &&
              fpiWaters
                .filter((w) => w.fpi >= fpiMin && w.geometry?.length >= 3 && w.name !== water?.name)
                .map((w, wi) => (
                  <Polygon
                    key={`fpi-${wi}-${w.name}`}
                    positions={w.geometry}
                    pathOptions={{ color: FPI_COLOR(w.fpi), weight: 1.5, fillColor: FPI_COLOR(w.fpi), fillOpacity: 0.3 }}
                  >
                    <Tooltip sticky>
                      <div data-testid={`fpi-tooltip-${w.name}`} style={{ minWidth: 170 }}>
                        <strong>
                          {w.name} – {t.fpi.tooltip} {w.fpi}/40
                        </strong>
                        {FPI_PART_KEYS.map((k) => (
                          <div key={k} style={{ fontSize: 11, fontWeight: k === "bpp" ? 700 : 400, color: k === "bpp" ? "#b45309" : undefined }}>
                            {t.fpi.parts[k]}: {w.parts?.[k]?.score ?? w.parts?.[k] ?? 3}/{k === "bpp" ? 10 : 5}
                          </div>
                        ))}
                      </div>
                    </Tooltip>
                  </Polygon>
                ))}
            {water && water.kind === "lake" && water.geometry.length >= 3 && (
              <Polygon
                positions={water.geometry}
                pathOptions={
                  fpiVisible && currentFpi && currentFpi.fpi < fpiMin
                    ? { color: "#8A998E", weight: 1.5, fillColor: "#8A998E", fillOpacity: 0.05, dashArray: "4 6" }
                    : {
                        color: fpiVisible && currentFpi ? FPI_COLOR(currentFpi.fpi) : "#7BC98C",
                        weight: 2,
                        fillColor: fpiVisible && currentFpi ? FPI_COLOR(currentFpi.fpi) : "#3E7CB1",
                        fillOpacity: fpiVisible && currentFpi ? 0.35 : 0.15,
                      }
                }
              >
                {fpiVisible && currentFpi && currentFpi.fpi >= fpiMin && (
                  <Tooltip sticky>
                    <div data-testid="fpi-tooltip-current" style={{ minWidth: 170 }}>
                      <strong>
                        {water.name} – {t.fpi.tooltip} {currentFpi.fpi}/40
                      </strong>
                      {FPI_PART_KEYS.map((k) => (
                        <div key={k} style={{ fontSize: 11, fontWeight: k === "bpp" ? 700 : 400, color: k === "bpp" ? "#b45309" : undefined }}>
                          {t.fpi.parts[k]}: {currentFpi.parts?.[k]?.score ?? currentFpi.parts?.[k] ?? 3}/{k === "bpp" ? 10 : 5}
                        </div>
                      ))}
                    </div>
                  </Tooltip>
                )}
              </Polygon>
            )}
            {water && water.kind === "river" && (
              <Polyline positions={water.geometry} pathOptions={{ color: "#3E7CB1", weight: 4 }} />
            )}
            {zones.map((z, i) => (
              <Polygon
                key={`${z.type}-${i}`}
                positions={z.polygon}
                pathOptions={{ color: z.color, weight: 1, fillColor: z.color, fillOpacity: 0.55 }}
              />
            ))}
            {/* Hotspot-strukturer: uddar, grund, vegetationskanter, habitatövergångar */}
            {visibleHotspots.map((h, i) => (
              <Marker key={`hs-${h.type}-${i}`} position={h.point} icon={hsIcon(h.type)}>
                <Tooltip sticky>
                  <div data-testid={`hotspot-tooltip-${h.type}-${i}`} style={{ maxWidth: 230 }}>
                    <strong>
                      {t.hs.types[h.type] || h.type}
                      {h.shore_m != null && <span style={{ fontWeight: 400, fontSize: 11 }}> · {h.shore_m} m</span>}
                    </strong>
                    <div style={{ fontSize: 11, marginTop: 3 }}>{h.motivation}</div>
                  </div>
                </Tooltip>
              </Marker>
            ))}
            {fkAreas.filter((a) => a.rings?.length > 0).map((a, i) => (
              <Polygon
                key={`fk-${i}`}
                positions={a.rings}
                pathOptions={{
                  color: FK_COLORS[a.category],
                  weight: a.covers_water ? 2.5 : 1.5,
                  fillColor: FK_COLORS[a.category],
                  fillOpacity: a.covers_water ? 0.25 : 0.08,
                  dashArray: a.covers_water ? null : "5 5",
                }}
              >
                <Tooltip permanent direction="center" className="fk-label" opacity={1}>
                  <span style={{ color: FK_LABEL_COLORS[a.category] }}>{a.name}</span>
                </Tooltip>
              </Polygon>
            ))}
            {fkAreas.filter((a) => a.point).map((a, i) => (
              <CircleMarker
                key={`fkp-${i}`}
                center={a.point}
                radius={5}
                pathOptions={{ color: FK_COLORS[a.category], weight: 2, fillColor: FK_COLORS[a.category], fillOpacity: 0.7 }}
              >
                <Tooltip permanent direction="top" offset={[0, -6]} className="fk-label" opacity={1}>
                  <span style={{ color: FK_LABEL_COLORS[a.category] }}>{a.name}</span>
                </Tooltip>
              </CircleMarker>
            ))}
            {/* Boendemarkörer */}
            {(stays?.stays || []).filter((s) => s.lat != null && s.lon != null).map((s, i) => (
              <CircleMarker
                key={s.id}
                center={[s.lat, s.lon]}
                radius={6}
                pathOptions={{ color: "#D9A441", weight: 2, fillColor: "#D9A441", fillOpacity: 0.75 }}
              >
                <Tooltip permanent direction="top" offset={[0, -7]} className="fk-label" opacity={1}>
                  <span data-testid={`stay-marker-label-${i}`} style={{ color: "#F2C979" }}>
                    {s.name || t.osmKinds?.[s.category] || s.category} · {s.distance_km.toFixed(1)} km
                  </span>
                </Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* BOENDEMODAL */}
      {staysOpen && (
        <div
          data-testid="stays-modal"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-[#0F1412]/80 p-4 backdrop-blur-sm"
          onClick={() => setStaysOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-y-auto border border-[#28332E] bg-[#0F1412] p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="overline-label text-[#C98755]">{stays?.water || water?.name}</p>
                <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight text-[#EBE9E0] sm:text-3xl">
                  {t.staysTitle}
                </h2>
              </div>
              <button
                onClick={() => setStaysOpen(false)}
                data-testid="stays-close-button"
                aria-label="Stäng"
                className="border border-[#28332E] p-2 text-[#8A998E] transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            {staysLoading && (
              <p data-testid="stays-loading" className="mt-10 flex items-center gap-3 text-sm text-[#8A998E]">
                <Loader2 className="h-4 w-4 animate-spin" /> {t.staysLoading}
              </p>
            )}

            {!staysLoading && stays?.stays?.length > 0 && (
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {stays.stays.map((s, i) => {
                  const kindLabel = t.osmKinds?.[s.category];
                  const displayName = s.name || kindLabel || s.category;
                  const categoryLabel = s.provider === "osm" ? kindLabel || s.category : s.category || t.stayTypes[s.type] || s.type;
                  return (
                  <div key={s.id} data-testid={`stay-card-${i}`} className="flex flex-col border border-[#28332E] bg-[#161D1A]">
                    <div className="relative overflow-hidden">
                      {s.image ? (
                        <img src={s.image} alt={displayName} loading="lazy" className="aspect-[16/9] w-full rounded-t-sm object-cover" />
                      ) : (
                        <div className="flex aspect-[16/9] w-full items-center justify-center rounded-t-sm bg-[#0F1412]">
                          <MapPin className="h-8 w-8 text-[#C98755]/60" strokeWidth={1.5} />
                        </div>
                      )}
                      {stays.demo && (
                        <span className="absolute left-3 top-3 rounded-full bg-[#0F1412]/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-[#EBE9E0]/80 backdrop-blur-sm">
                          {t.exampleBadge}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <p className="overline-label text-[#9E643E]">
                        {displayName === categoryLabel ? "" : `${categoryLabel} · `}{t.providerNames[s.provider] || s.provider}
                        {s.rating ? ` · ★ ${s.rating}` : ""}
                      </p>
                      <h3 className="mt-2 font-serif text-lg font-medium leading-snug text-[#EBE9E0]">{displayName}</h3>
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-[#C98755]">
                        <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
                        {s.distance_km != null ? `${s.distance_km.toFixed(1)} ${t.kmFrom}` : s.area || ""}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-[#EBE9E0]/70">{s.subheading || (s.provider === "osm" ? t.osmDesc : t.stayDescs[s.type]) || ""}</p>
                      {s.price_from != null && (
                        <p className="mt-3 text-sm font-semibold text-[#EBE9E0]">{t.priceFrom(s.price_from)}</p>
                      )}
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`stay-book-button-${i}`}
                        className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#C98755] px-5 py-2.5 text-xs font-semibold tracking-wide text-[#0F1412] transition-colors duration-300 hover:bg-[#F6F5F0]"
                      >
                        {s.provider === "osm" ? (s.url?.includes("openstreetmap.org") ? t.showOnMap : t.visitSite) : t.book} →
                      </a>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {!staysLoading && stays && stays.stays?.length === 0 && (
              <p data-testid="stays-empty" className="mt-10 text-sm text-[#8A998E]">{t.staysEmpty}</p>
            )}

            {!staysLoading && stays?.search_links?.length > 0 && (
              <div data-testid="stays-search-links" className="mt-6 border-t border-[#28332E] pt-4">
                <p className="overline-label text-[#8A998E]">{t.moreStays}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {stays.search_links.map((l) => (
                    <a
                      key={l.label}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`stay-search-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                      className="rounded-full border border-[#28332E] px-4 py-2 text-xs font-semibold text-[#EBE9E0]/80 transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
                    >
                      {l.label} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}

            {!staysLoading && !stays && (
              <p data-testid="stays-error" className="mt-10 text-sm text-red-300">{t.staysError}</p>
            )}

            {!staysLoading && stays?.stays?.length > 0 && (
              <p data-testid="stays-map-hint" className="mt-6 border-t border-[#28332E] pt-4 text-xs text-[#8A998E]">
                {t.staysMapHint}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
